import syncController from '../../controllers/sync.controller.js'
import ticketController from '../../controllers/tickets/ticket.controller.js'

import { useAuthStore } from '../../stores/authStore.js'
import { useTicketStore } from '../../stores/tickets/ticketStore.js'

import cacheUtility from '../../utils/cache.utility.js'

import { useTicketSettingsHosts } from './useTicketSettingsHosts.js'

export function useTickets() {
  const authStore = useAuthStore()
  const ticketStore = useTicketStore()
  const { hydrateForTickets } = useTicketSettingsHosts()

  async function afterActiveLoad(res) {
    if (res.settingsHosts && Object.keys(res.settingsHosts).length) {
      ticketStore.mergeSettingsHosts(res.settingsHosts)
    }
    await hydrateForTickets(res.tickets)
  }

  async function afterArchivedLoad(res) {
    if (res.settingsHosts && Object.keys(res.settingsHosts).length) {
      ticketStore.mergeSettingsHosts(res.settingsHosts)
    }
    await hydrateForTickets(res.tickets)
  }

  async function syncGenerations() {
    const patch = await syncController.get.generations()
    if (patch) {
      authStore.hydrate.user({
        ...authStore.user,
        accountGenerations: cacheUtility.merge.generations(authStore.user.accountGenerations, patch),
      })
    }
  }

  function apiFiltersFromMeta(sliceMeta) {
    const filters = { ...(sliceMeta?.filters ?? {}) }
    const apiFilters = { ...filters }
    delete apiFilters.view
    return apiFilters
  }

  function hasScopedAccounts(meta) {
    return Boolean(meta?.ids?.length)
  }

  function hydrateEmptySlice(slice, meta, filters, view) {
    const payload = {
      items: [],
      _meta: cacheUtility.meta({
        ...meta,
        cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash({ ...filters, view })),
        filters: { ...filters, view },
        total: 0,
      }),
    }
    if (slice === 'active') ticketStore.hydrate.active(payload)
    else ticketStore.hydrate.archived(payload)
  }

  /**
   * Load active tickets when the active slice is stale. Skips the network
   * call when `cacheUtility.fresh` says the bucket is still current. Leaves
   * the slice untouched when the load fails (controller toasts and returns
   * `null`).
   *
   * @param {object} [filters={}]
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<void>}
   */
  async function loadActiveTickets(filters = {}, meta) {
    await syncGenerations()
    if (cacheUtility.fresh(ticketStore.active._meta, { ...meta, generations: authStore.user.accountGenerations })) return
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('active', meta, filters, 'active')
      return
    }

    const res = await ticketController.get.active({ ...filters, accountIds: meta.ids })
    if (res) {
      ticketStore.hydrate.active({
        items: res.tickets,
        _meta: cacheUtility.meta({
          ...meta,
          cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash({ ...filters, view: 'active' })),
          filters: { ...filters, view: 'active' },
          total: res.totalLength,
        }),
      })
      await afterActiveLoad(res)
    }
  }

  /**
   * Load active tickets unconditionally — bypasses the fresh check. Leaves the
   * slice untouched when the load fails (controller toasts and returns `null`).
   *
   * @param {object} [filters={}]
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<void>}
   */
  async function forceLoadActiveTickets(filters = {}, meta) {
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('active', meta, filters, 'active')
      return
    }

    const res = await ticketController.get.active({ ...filters, accountIds: meta.ids })
    if (res) {
      ticketStore.hydrate.active({
        items: res.tickets,
        _meta: cacheUtility.meta({
          ...meta,
          cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash({ ...filters, view: 'active' })),
          filters: { ...filters, view: 'active' },
          total: res.totalLength,
        }),
      })
      await afterActiveLoad(res)
    }
  }

  /**
   * Reconcile the active ticket list when stale — sync generations, refetch,
   * merge by id, re-hydrate. Skips when there is no session or the slice was
   * never loaded.
   *
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<boolean>} `true` when a refetch ran, `false` otherwise.
   */
  async function updateActiveTickets(meta) {
    if (!authStore.user) return false

    const slice = ticketStore.active
    const savedFilters = slice._meta?.filters ? { ...slice._meta.filters } : null

    await syncGenerations()

    if (!slice._meta) {
      await forceLoadActiveTickets(apiFiltersFromMeta(savedFilters), meta)
      return true
    }

    const filters = { ...(slice._meta.filters ?? {}), view: 'active' }
    const apiFilters = { ...filters }
    delete apiFilters.view

    if (cacheUtility.fresh(slice._meta, { ...meta, generations: authStore.user.accountGenerations })) return false
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('active', meta, apiFilters, 'active')
      return true
    }

    const res = await ticketController.get.active({ ...apiFilters, accountIds: meta.ids })
    if (!res) return false

    const items = slice.items?.length ? cacheUtility.merge.id(slice.items, res.tickets) : res.tickets
    ticketStore.hydrate.active({
      items,
      _meta: cacheUtility.meta({
        ...meta,
        cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash(filters)),
        filters,
        total: res.totalLength,
      }),
    })
    await afterActiveLoad(res)
    return true
  }

  /**
   * Load archived tickets when the archived slice is stale. Skips the network
   * call when `cacheUtility.fresh` says the bucket is still current. Leaves
   * the slice untouched when the load fails (controller toasts and returns
   * `null`).
   *
   * @param {object} [filters={}]
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<void>}
   */
  async function loadArchivedTickets(filters = {}, meta) {
    await syncGenerations()
    if (cacheUtility.fresh(ticketStore.archived._meta, { ...meta, generations: authStore.user.accountGenerations })) return
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('archived', meta, filters, 'archived')
      return
    }

    const res = await ticketController.get.archived({ ...filters, accountIds: meta.ids })
    if (res) {
      ticketStore.hydrate.archived({
        items: res.tickets,
        _meta: cacheUtility.meta({
          ...meta,
          cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash({ ...filters, view: 'archived' })),
          filters: { ...filters, view: 'archived' },
          total: res.totalLength,
        }),
      })
      await afterArchivedLoad(res)
    }
  }

  /**
   * Load archived tickets unconditionally — bypasses the fresh check. Leaves
   * the slice untouched when the load fails (controller toasts and returns
   * `null`).
   *
   * @param {object} [filters={}]
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<void>}
   */
  async function forceLoadArchivedTickets(filters = {}, meta) {
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('archived', meta, filters, 'archived')
      return
    }

    const res = await ticketController.get.archived({ ...filters, accountIds: meta.ids })
    if (res) {
      ticketStore.hydrate.archived({
        items: res.tickets,
        _meta: cacheUtility.meta({
          ...meta,
          cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash({ ...filters, view: 'archived' })),
          filters: { ...filters, view: 'archived' },
          total: res.totalLength,
        }),
      })
      await afterArchivedLoad(res)
    }
  }

  /**
   * Reconcile the archived ticket list when stale — sync generations, refetch,
   * merge by id, re-hydrate. Skips when there is no session or the slice was
   * never loaded.
   *
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} meta
   * @returns {Promise<boolean>} `true` when a refetch ran, `false` otherwise.
   */
  async function updateArchivedTickets(meta) {
    if (!authStore.user) return false

    const slice = ticketStore.archived
    const savedFilters = slice._meta?.filters ? { ...slice._meta.filters } : null

    await syncGenerations()

    if (!slice._meta) {
      await forceLoadArchivedTickets(apiFiltersFromMeta(savedFilters), meta)
      return true
    }

    const filters = { ...(slice._meta.filters ?? {}), view: 'archived' }
    const apiFilters = { ...filters }
    delete apiFilters.view

    if (cacheUtility.fresh(slice._meta, { ...meta, generations: authStore.user.accountGenerations })) return false
    if (!hasScopedAccounts(meta)) {
      hydrateEmptySlice('archived', meta, apiFilters, 'archived')
      return true
    }

    const res = await ticketController.get.archived({ ...apiFilters, accountIds: meta.ids })
    if (!res) return false

    const items = slice.items?.length ? cacheUtility.merge.id(slice.items, res.tickets) : res.tickets
    ticketStore.hydrate.archived({
      items,
      _meta: cacheUtility.meta({
        ...meta,
        cacheKey: cacheUtility.key(meta.scopeKey, meta.domain, cacheUtility.hash(filters)),
        filters,
        total: res.totalLength,
      }),
    })
    await afterArchivedLoad(res)
    return true
  }

  return {
    loadActiveTickets,
    forceLoadActiveTickets,
    updateActiveTickets,

    loadArchivedTickets,
    forceLoadArchivedTickets,
    updateArchivedTickets,
  }
}
